import { Avatar, BasePagination, HStack, IconButtonWithTooltip, PlusIcon, Text, VStack } from 'design-system';
import { useGetCmsRestaurantsApi } from 'apis/restaurants/use-get-cms-restaurants-api';
import { BaseTable, BaseTableContainer, PhoneNumber } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { AccountManagerFilterButton, NameFilterButton } from 'components/base-table/filters';
import { BaseSortButton } from 'components/base-table/sorting';
import { Can } from 'configs/can';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { AccountManagerCell, IAccountManagerCell } from 'pages/restaurants/components/account-manager-cell';
import { CreateRestaurantModal } from 'pages/restaurants/components/create-restaurant/create-restaurant-modal';
import {
  IRestaurantActionsCell,
  RestaurantActionsCell,
} from 'pages/restaurants/components/restaurant-actions-cell';
import { RestaurantListRow, toRestaurantRow } from 'pages/restaurants/utils/to-restaurant-row';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { CellProps } from 'react-table';

// Served by UMS (api/v1/restaurants) since the account-manager work; the old backend CRUD
// list is no longer used by this page. Sorting/filtering stay URL-param driven.
export const RestaurantsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, data } = useGetCmsRestaurantsApi();

  const { pageCount = 0, total = 0, data: restaurantsData } = data ?? {};

  useEffect(() => {
    dispatch(changeTitle('sidebar.restaurants'));
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Restaurant Name'
            filterButtonComponent={<NameFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='nameEn' />}
          />
        ),
        accessor: 'name',
        Cell: ({ value }: CellProps<any, RestaurantListRow['name']>) => (
          <HStack spacing='md'>
            <Avatar size='sm' name={value?.name} src={value?.imageUrl ?? undefined} />
            <Link
              to={`/app/restaurants/details/${value?.id}`}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}>
              <Text fontWeight='semibold' color='gray.600'>
                {value?.name}
              </Text>
            </Link>
          </HStack>
        ),
      },
      {
        Header: <TableHeader name='Admin Info' />,
        accessor: 'admin',
        Cell: ({ value }: CellProps<any, RestaurantListRow['admin']>) =>
          value ? (
            <VStack align='start' spacing='0'>
              {value.phoneNumber ? (
                <Link
                  to={`/app/app-users/list?limit=45&page=1&phoneNumber=${encodeURIComponent(value.phoneNumber)}`}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}>
                  <Text fontWeight='semibold' color='gray.600'>
                    {value.name || '---'}
                  </Text>
                </Link>
              ) : (
                <Text fontWeight='semibold' color='gray.600'>
                  {value.name || '---'}
                </Text>
              )}
              <Text fontSize='sm' color='gray.500'>
                {value.email || '---'}
              </Text>
              {value.phoneNumber && <PhoneNumber value={value.phoneNumber} showWhatsApp={false} />}
            </VStack>
          ) : (
            <TableCell value='---' />
          ),
      },
      {
        Header: <TableHeader name='Account Manager' filterButtonComponent={<AccountManagerFilterButton />} />,
        accessor: 'accountManager',
        Cell: ({ value }: CellProps<any, RestaurantListRow['accountManager']>) => (
          <CustomTableCell<IAccountManagerCell> as={AccountManagerCell} accountManager={value} />
        ),
      },
      {
        Header: (
          <TableHeader name='VAT Value' sortButtonComponent={<BaseSortButton sortBy='vatValue' />} />
        ),
        accessor: 'vatValue',
        Cell: ({ value }: CellProps<any, RestaurantListRow['vatValue']>) => <TableCell value={value ?? '---'} />,
      },
      {
        Header: (
          <TableHeader name='Created At' sortButtonComponent={<BaseSortButton sortBy='createdAt' />} />
        ),
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, RestaurantListRow['createdAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, RestaurantListRow['actions']>) => (
          <CustomTableCell<IRestaurantActionsCell> as={RestaurantActionsCell} restaurant={value} />
        ),
      },
    ],
    []
  );

  const columnsAccessorData = React.useMemo(() => (restaurantsData ?? []).map(toRestaurantRow), [restaurantsData]);

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='sidebar.restaurants' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />

          <Can I='update' a='User'>
            <CreateRestaurantModal
              trigger={
                <IconButtonWithTooltip
                  label='Create restaurant'
                  aria-label='create-restaurant-btn'
                  Icon={<PlusIcon />}
                />
              }
            />
          </Can>
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={columnsAccessorData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
